import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Faq() {
  const { t } = useTranslation("global");
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  
  const faqData = [
    { title: t("FAQ.title") },
    { question: t("FAQ.Q1"), answer: t("FAQ.R1") },
    { question: t("FAQ.Q2"), answer: t("FAQ.R2") },
    { question: t("FAQ.Q3"), answer: t("FAQ.R3") },
    { question: t("FAQ.Q4"), answer: t("FAQ.R4") },
    { question: t("FAQ.Q5"), answer: t("FAQ.R5") },
    { question: t("FAQ.Q6"), answer: t("FAQ.R6") },
    { question: t("FAQ.Q7"), answer: t("FAQ.R7") },
    { question: t("FAQ.Q8"), answer: t("FAQ.R8") },
    { question: t("FAQ.Q9"), answer: t("FAQ.R9") },
    { question: t("FAQ.Q10"), answer: t("FAQ.R10") },
  ];

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const FAQItem = ({ 
    question, 
    answer, 
    isOpen, 
    onClick 
  }: { 
    question: string; 
    answer: string; 
    isOpen: boolean; 
    onClick: () => void;
  }) => (
    <div className="bg-white rounded-xl overflow-hidden mb-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
      <button
        className="w-full text-left p-5 flex justify-between items-center focus:outline-none"
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <span className="font-heading font-bold text-lg text-gray-800">{question}</span>
        <div className={`p-2 rounded-full transition-colors duration-300 ${isOpen ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
          {isOpen ? (
            <ChevronUp className="h-5 w-5" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-5 w-5" aria-hidden="true" />
          )}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
        <div className="p-5 bg-gray-50 border-t border-gray-100">
          <p className="text-gray-700">{answer}</p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="md:w-1/2 space-y-6">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-black">
                {faqData[0].title}
              </h1>
              <p className="text-lg text-gray-600 max-w-xl">
                {t("FAQ.subtitle", "Trouvez les réponses à vos questions sur nos services d'évaluation de conformité")}
              </p>
              <div className="w-20 h-1 bg-blue-600"></div>
            </div>
            
            <div className="md:w-1/2 flex justify-center">
              {/* FAQ Illustration - Tailwind Only */}
              <div className="relative w-80 h-80 mx-auto">
                {/* Background decoration */}
                <div className="absolute w-64 h-64 bg-blue-100 rounded-full -top-10 -right-10 opacity-40"></div>
                
                {/* Main question mark design */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg flex items-center justify-center z-20">
                  <span className="text-white text-8xl font-bold">?</span>
                </div>
                
                {/* Small question bubbles */}
                <div className="absolute top-6 left-6 w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-blue-700 text-2xl font-bold">?</span>
                </div>
                
                <div className="absolute top-10 right-16 w-10 h-10 bg-blue-300 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-white text-xl font-bold">?</span>
                </div>
                
                <div className="absolute bottom-12 left-14 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-blue-500 text-xl font-bold">?</span>
                </div>
                
                <div className="absolute bottom-10 right-10 w-14 h-14 bg-blue-400 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-white text-2xl font-bold">?</span>
                </div>
                
                {/* Chat bubble elements */}
                <div className="absolute top-1/4 -right-4 bg-white rounded-lg p-3 shadow-md transform rotate-3">
                  <div className="w-32">
                    <div className="w-full h-2 bg-gray-200 rounded-full mb-2"></div>
                    <div className="w-3/4 h-2 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="absolute -bottom-2 right-4 w-4 h-4 bg-white transform rotate-45"></div>
                </div>
                
                <div className="absolute bottom-1/4 -left-4 bg-white rounded-lg p-3 shadow-md transform -rotate-3">
                  <div className="w-32">
                    <div className="w-full h-2 bg-gray-200 rounded-full mb-2"></div>
                    <div className="w-4/5 h-2 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="absolute -bottom-2 left-4 w-4 h-4 bg-white transform rotate-45"></div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute top-3/4 right-1/4 w-6 h-6 bg-blue-50 rounded-full"></div>
                <div className="absolute top-1/3 left-1/3 w-4 h-4 bg-blue-100 rounded-full"></div>
                <div className="absolute bottom-1/4 right-1/3 w-8 h-8 bg-blue-50 rounded-full"></div>
                
                {/* Search icon bubble */}
                <div className="absolute top-2/3 right-0 w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-black">
              {t("FAQ.commonQuestions", "Questions fréquemment posées")}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">
              {t("FAQ.helpText", "Vous ne trouvez pas ce que vous cherchez? N'hésitez pas à nous contacter directement.")}
            </p>
            <div className="w-20 h-1 bg-blue-600 mx-auto mt-4"></div>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {faqData.slice(1).map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index}
                onClick={() => toggleQuestion(index)}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-12 bg-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
            {t("FAQ.stillHaveQuestions", "Vous avez encore des questions?")}
          </h2>
          <p className="text-blue-100 max-w-2xl mx-auto mb-8">
            {t("FAQ.contactUs", "Notre équipe d'experts est à votre disposition pour répondre à toutes vos questions sur nos services.")}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="contact"
              className="bg-white text-blue-800 hover:bg-blue-100 py-3 px-8 rounded-full font-heading font-bold transition-all"
            >
              {t("contact.contact")}
            </a>
            <a
              href="demo"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 py-3 px-8 rounded-full font-heading font-bold transition-all"
            >
              {t("header.demo")}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}